import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Car, Palette, User, Hash, Info, Check, MapPin, ChevronRight } from "lucide-react";
import { getProfile, saveProfile, sanitize, validate } from "../lib/driverProfile";
import { getCarLocation, formatRelativeTime } from "../lib/carLocation";
import { useBackdropDismiss } from "../lib/useBackdropDismiss";
const COLOR_SUGGESTIONS = ["White", "Black", "Grey", "Silver", "Blue", "Red", "Green", "Yellow"];

/**
 * Driver identity modal.
 *  - mode="required": user must fill in to proceed (no dismiss button).
 *  - mode="edit": user can close without saving; can also see current values.
 */
export default function DriverIdentityModal({ open, mode = "edit", onSaved, onClose, onOpenCarLocation }) {
  const dismissProps = useBackdropDismiss(open, onClose);
  const [values, setValues] = React.useState(() => {
    const p = getProfile();
    // Strip any legacy full_plate so it never re-enters state or submits.
    return p
      ? { nickname: p.nickname || "", car_color: p.car_color || "", car_model: p.car_model || "", plate_last3: p.plate_last3 || "" }
      : { nickname: "", car_color: "", car_model: "", plate_last3: "" };
  });
  const [err, setErr] = React.useState(null);
  const [carLoc, setCarLoc] = React.useState(() => getCarLocation());

  React.useEffect(() => {
    if (open) {
      const p = getProfile();
      setValues(
        p
          ? { nickname: p.nickname || "", car_color: p.car_color || "", car_model: p.car_model || "", plate_last3: p.plate_last3 || "" }
          : { nickname: "", car_color: "", car_model: "", plate_last3: "" }
      );
      setCarLoc(getCarLocation());
      setErr(null);
    }
  }, [open]);

  const set = (k) => (e) => setValues((v) => ({ ...v, [k]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    // full_plate is intentionally omitted — never collected or persisted from the UI.
    const clean = sanitize({ ...values, full_plate: "" });
    const message = validate(clean);
    if (message) { setErr(message); return; }
    try {
      const saved = saveProfile(clean);
      onSaved && onSaved(saved);
    } catch (ex) {
      setErr(ex.message || "Could not save profile.");
    }
  };

  const required = mode === "required";

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[3500] bg-black/75"
            {...dismissProps}
            data-testid="driver-modal-backdrop"
          />
          <motion.form
            onSubmit={submit}
            initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 280 }}
            className="fixed inset-x-0 bottom-0 z-[3501] bg-[#141416] border-t border-white/10 rounded-t-3xl p-5 pb-8 max-h-[92vh] overflow-y-auto sm:left-1/2 sm:right-auto sm:-translate-x-1/2 sm:bottom-6 sm:rounded-3xl sm:border sm:w-[440px] sm:max-w-[92vw]"
            data-testid="driver-modal"
          >
            <div className="flex items-start justify-between mb-4 gap-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#ccff00] font-semibold">
                  {required ? "One quick step" : "Edit your driver details"}
                </div>
                <div className="font-heading text-xl font-semibold text-white mt-1 leading-tight">
                  {required ? "Who are you on the road?" : "Your driver profile"}
                </div>
                <p className="text-[12px] text-zinc-400 mt-1 leading-snug">
                  We only use this so the other driver can identify your car.
                  {required
                    ? " Stored on this device only — no signup, no account."
                    : " Update the details other drivers see when you drop or claim a spot."}
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="shrink-0 w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center active:scale-95 transition-transform"
                data-testid="driver-modal-close"
                aria-label={required ? "Cancel and go back" : "Close"}
              >
                <X className="w-4 h-4 text-zinc-400" />
              </button>
            </div>

            <Field
              label="Nickname"
              icon={<User className="w-3.5 h-3.5" />}
              testid="driver-nickname"
              placeholder="e.g. Steve"
              value={values.nickname}
              onChange={set("nickname")}
              autoComplete="nickname"
              maxLength={30}
            />

            <div className="grid grid-cols-2 gap-2 mt-3">
              <Field
                label="Car color"
                icon={<Palette className="w-3.5 h-3.5" />}
                testid="driver-color"
                placeholder="White"
                value={values.car_color}
                onChange={set("car_color")}
                maxLength={30}
              />
              <Field
                label="Car model"
                icon={<Car className="w-3.5 h-3.5" />}
                testid="driver-model"
                placeholder="Fiat 500"
                value={values.car_model}
                onChange={set("car_model")}
                maxLength={40}
              />
            </div>

            <div className="mt-2 flex flex-wrap gap-1.5">
              {COLOR_SUGGESTIONS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setValues((v) => ({ ...v, car_color: c }))}
                  className={`text-[11px] px-2 h-7 rounded-full border transition-colors ${
                    values.car_color?.toLowerCase() === c.toLowerCase()
                      ? "bg-[#ccff00] border-[#ccff00] text-[#0a0a0c] font-semibold"
                      : "bg-[#0a0a0c] border-white/10 text-zinc-300"
                  }`}
                  data-testid={`driver-color-chip-${c.toLowerCase()}`}
                >
                  {c}
                </button>
              ))}
            </div>

            <div className="mt-4">
              <Field
                label="Plate — last 3 characters"
                icon={<Hash className="w-3.5 h-3.5" />}
                testid="driver-plate3"
                placeholder="ABC"
                value={values.plate_last3}
                onChange={(e) => setValues((v) => ({ ...v, plate_last3: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 3) }))}
                maxLength={3}
                monospace
                uppercase
              />
              <div className="mt-1.5 p-2 rounded-lg bg-[#0a0a0c] border border-white/5 flex items-start gap-1.5 text-[11px] text-zinc-400">
                <Info className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[#ccff00]" />
                <span>Last 3 characters is enough to help the other driver identify you. Your full plate stays private.</span>
              </div>
            </div>

            {err && (
              <div className="mt-4 p-2.5 rounded-lg bg-red-500/10 border border-red-500/40 text-red-200 text-[12px]" data-testid="driver-error">
                {err}
              </div>
            )}

            <button
              type="submit"
              className="mt-5 w-full h-12 rounded-full bg-[#ccff00] text-[#0a0a0c] font-semibold text-sm inline-flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
              data-testid="driver-save"
            >
              <Check className="w-4 h-4" />
              {required ? "Save and continue" : "Save changes"}
            </button>

            <button
              type="button"
              onClick={onClose}
              className="mt-2 w-full h-11 rounded-full bg-transparent border border-white/10 text-zinc-300 text-[13px] font-medium inline-flex items-center justify-center gap-1.5 active:scale-[0.98] transition-transform"
              data-testid="driver-cancel"
            >
              {required ? "Back" : "Cancel"}
            </button>

            <div className="mt-3 text-[10px] text-zinc-500 text-center leading-relaxed">
              {required
                ? "You'll need to fill this in before you can drop or claim a spot."
                : "Stored only on this device. No signup, no account."}
            </div>

            {!required && onOpenCarLocation && (
              <button
                type="button"
                onClick={() => onOpenCarLocation()}
                className="mt-4 w-full flex items-center gap-3 px-3 py-3 rounded-2xl bg-[#0a0a0c] border border-white/10 hover:border-white/20 active:scale-[0.99] transition-all text-left"
                data-testid="driver-open-car-location"
              >
                <div className="w-9 h-9 rounded-xl bg-[#ccff00]/10 border border-[#ccff00]/30 flex items-center justify-center shrink-0">
                  <MapPin className="w-4 h-4 text-[#ccff00]" strokeWidth={2.5} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-semibold text-white">Car location</div>
                  <div className="text-[11px] text-zinc-400 mt-0.5" data-testid="driver-car-location-status">
                    {carLoc ? `Saved ${formatRelativeTime(carLoc.saved_at)}` : "Save where you parked"}
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-zinc-500 shrink-0" />
              </button>
            )}
          </motion.form>
        </>
      )}
    </AnimatePresence>
  );
}

function Field({ label, icon, testid, placeholder, value, onChange, maxLength, monospace, uppercase, autoComplete }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-semibold inline-flex items-center gap-1.5">
        {icon} {label}
      </span>
      <input
        type="text"
        placeholder={placeholder}
        value={value || ""}
        onChange={onChange}
        maxLength={maxLength}
        autoComplete={autoComplete || "off"}
        autoCapitalize={uppercase ? "characters" : "words"}
        className={`mt-1.5 w-full h-11 px-3 rounded-xl bg-[#0a0a0c] border border-white/10 text-white text-sm outline-none focus:border-[#ccff00]/60 focus:bg-[#0a0a0c] transition-colors ${
          monospace ? "font-mono tracking-widest" : ""
        } ${uppercase ? "uppercase" : ""}`}
        data-testid={testid}
      />
    </label>
  );
}
